/**
 * 🏗️ PollService Unifié - Service Principal pour tous les types de polls
 *
 * STORY 2.1 - Service Poll Unifié
 * ==================================
 *
 * OBJECTIF : Remplacer les 4 services séparés (DatePollService, FormPollService,
 * QuizzService, AvailabilityPollsService) par un service unifié.
 *
 * ARCHITECTURE :
 * - Interface commune pour tous les types de polls
 * - Délégation vers services spécialisés si nécessaire
 * - Intégration directe Supabase
 * - Validation et transformation unifiées
 * - Cache et optimisations partagées
 *
 * ⚠️ RÈGLES DE STOCKAGE CRITIQUES ⚠️
 * ===================================
 *
 * 🚫 INTERDIT : localStorage pour données métier (polls, votes, réponses)
 * ✅ OBLIGATOIRE : Supabase uniquement pour toutes les données métier
 *
 * POURQUOI ?
 * - Cohérence des données (une seule source de vérité)
 * - Accès multi-device (données accessibles partout)
 * - Sécurité (RLS Supabase protège les données)
 * - Collaboration (plusieurs utilisateurs peuvent voir les mêmes données)
 * - Pas de perte de données (localStorage peut être effacé)
 *
 * EN CAS D'ERREUR SUPABASE :
 * 1. Logger l'erreur avec logError()
 * 2. Afficher un message clair à l'utilisateur
 * 3. Retourner null ou throw selon le contexte
 * 4. ❌ NE JAMAIS utiliser localStorage comme fallback
 *
 * DOCUMENTATION :
 * - Docs/ARCHITECTURE/STORAGE-ARCHITECTURE-FINAL.md
 * - Docs/DEVELOPMENT-RULES.md
 */

import { supabase } from "@core/lib/supabase";
import i18nInstance from "@core/i18n";
import { logger } from "@core/lib/logger";
import { ErrorFactory, logError } from "@core/lib/error-handling";
import { normalizePollSettings } from "@core/lib/settingsNormalize";
import { stripEmailFields } from "@core/lib/inviteEmails";
import {
  getGuestOwnerToken,
  getOwnerTokenForPoll,
  removeKeyringKeys,
} from "@core/lib/guestIdentityService";
import type { Poll, PollCreateInput } from "@core/lib/pollStorageUnified";
import { createPoll as createPollViaEdgeFunction } from "@core/lib/edgeFunctionClient"; // NOTE: Poll legacy gardé pour compatibilité

// Types unifiés pour tous les polls
export type PollType = "date" | "form" | "quizz" | "availability";

/**
 * Fusionne une row `polls` (DB) avec son `poll_data_json` → objet unifié.
 * Les colonnes top-level de la table = source de vérité (écrasent poll_data_json).
 * Pure function extraite de getPoll pour test (bug "Modifier" 01/06).
 */
export function mergePollRow(
  data: Record<string, any>,
): Record<string, unknown> {
  const pollData = data.poll_data_json || {};
  return {
    ...pollData,
    id: data.id,
    title: data.title || pollData.title,
    slug: data.slug || pollData.slug,
    type: data.type || pollData.type,
    status: data.status || pollData.status,
    creator_id: data.creator_id ?? pollData.creator_id ?? null,
    creator_type: data.creator_type ?? pollData.creator_type,
    guest_fingerprint:
      data.guest_fingerprint ?? pollData.guest_fingerprint ?? null,
    created_at: data.created_at,
    updated_at: data.updated_at,
    // Remonter les champs stockés dans poll_data_json au top-level
    // (mode Typeform, thème, visibilité résultats, règles conditionnelles)
    displayMode: pollData.displayMode,
    themeId: pollData.themeId,
    resultsVisibility: pollData.resultsVisibility,
    conditionalRules: pollData.conditionalRules,
  };
}

/**
 * Clés d'identité du créateur qui ne doivent JAMAIS entrer dans
 * `poll_data_json` : ce champ est renvoyé aux visiteurs des pages de vote.
 * Elles vivent dans les colonnes dédiées de `polls` (source de vérité, et
 * seules lues côté serveur). Le trigger `polls_strip_identity_from_json_trg`
 * (migration 20260928100000) les retire aussi côté base ; ce filtre évite de
 * les envoyer du tout.
 */
const CREATOR_IDENTITY_KEYS = [
  "guest_fingerprint",
  "guest_session_id",
  "creator_id",
] as const;

export function stripCreatorIdentity<T extends Record<string, unknown>>(
  data: T,
): T {
  const out: Record<string, unknown> = { ...data };
  for (const key of CREATOR_IDENTITY_KEYS) delete out[key];
  return out as T;
}

/**
 * Identité du créateur lue sur les COLONNES d'une row `polls`, pour les
 * fusions qui étalent `poll_data_json` : ce JSON ne la porte plus.
 */
function creatorIdentityFromRow(row: Record<string, any>) {
  return {
    creator_id: row.creator_id ?? null,
    creator_type: row.creator_type ?? row.poll_data_json?.creator_type,
    guest_fingerprint: row.guest_fingerprint ?? null,
  };
}

/**
 * Fusionne les `updates` d'une édition dans le `poll_data_json` existant.
 * Invariant R2 (régression bloquante) : les champs absents des updates — en
 * particulier `questions` d'un formulaire — DOIVENT être préservés (ne pas
 * écraser poll_data_json en entier). Les champs présents dans updates écrasent.
 * Pure function extraite de updatePoll pour test de non-régression.
 */
export function mergeUpdatesIntoPollData(
  existing: Record<string, unknown> | null | undefined,
  updates: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = {
    ...(existing || {}),
    ...updates,
  };

  // `settings` est fusionné EN PROFONDEUR : une fusion de surface remplaçait
  // le bloc entier, donc toute clé absente des updates disparaissait
  // silencieusement (lieu, expiration choisie, nombre max de réponses,
  // visibilité des résultats, accès sur invitation…). Vérifié en prod le
  // 2026-09-16 : changer le seul titre ramenait l'expiration à sa valeur par
  // défaut. Les clés présentes dans les updates gagnent, comme avant.
  const existingSettings = (existing as { settings?: unknown } | null)
    ?.settings;
  const updatedSettings = (updates as { settings?: unknown }).settings;
  if (
    existingSettings &&
    typeof existingSettings === "object" &&
    !Array.isArray(existingSettings) &&
    updatedSettings &&
    typeof updatedSettings === "object" &&
    !Array.isArray(updatedSettings)
  ) {
    // `undefined` = « non renseigné par l'écran », pas « efface la valeur » :
    // buildPollSettings pose maxResponses/eventLocation à undefined quand
    // l'utilisateur ne les a pas saisis. Les garder effacerait la valeur
    // existante à la sérialisation JSON (la clé disparaît).
    const cleanUpdates = Object.fromEntries(
      Object.entries(updatedSettings as Record<string, unknown>).filter(
        ([, v]) => v !== undefined,
      ),
    );
    merged.settings = {
      ...(existingSettings as Record<string, unknown>),
      ...cleanUpdates,
    };
  }

  // Même normalisation qu'à la création (usePolls) : l'édition est l'autre
  // porte d'entrée de poll_data_json, et une clé doublon réintroduite ici
  // rouvrirait l'écart entre ce que l'UI promet et ce que le serveur applique.
  if (merged.settings && typeof merged.settings === "object") {
    merged.settings = normalizePollSettings(
      merged.settings as Record<string, unknown>,
    );
  }

  // Même frontière qu'à la création (usePolls) : aucune liste d'adresses
  // dans poll_data_json, renvoyé aux visiteurs. Les invités vivent dans
  // poll_invitations (createPollInvitations côté appelant).
  const withoutEmails = stripEmailFields(merged) as Record<string, unknown>;
  if (merged.settings && typeof merged.settings === "object") {
    withoutEmails.settings = stripEmailFields(
      merged.settings as Record<string, any>,
    );
  }

  return stripCreatorIdentity(withoutEmails);
}

export interface BasePoll {
  id: string;
  title: string;
  description?: string;
  slug: string;
  type: PollType;
  creator_id: string | null;
  status: "draft" | "active" | "closed" | "archived";
  created_at: string;
  updated_at: string;
  settings?: Record<string, any>;
  expires_at?: string;
  /** Template de branding assigné (couleurs/logo) — cf. task.md Story 1. */
  template?: {
    id: string;
    name: string;
    primary_color: string;
    secondary_color: string;
    logo_url: string | null;
  } | null;
}

// Types spécifiques par type de poll
export interface DatePollData extends BasePoll {
  type: "date";
  selectedDates: string[];
  timeSlotsByDate?: Record<
    string,
    Array<{
      hour: number;
      minute: number;
      enabled: boolean;
      duration?: number;
    }>
  >;
  dateGroups?: Array<{
    dates: string[];
    label: string;
    type: "weekend" | "range" | "custom";
  }>;
  showTimeSlots?: boolean;
  notificationsEnabled?: boolean;
  participantEmails?: string[];
  invitedEmails?: string[];
}

export interface FormPollData extends BasePoll {
  type: "form";
  questions: Array<{
    id: string;
    text: string;
    title: string;
    type:
      | "single"
      | "multiple"
      | "text"
      | "long-text"
      | "rating"
      | "nps"
      | "matrix"
      | "date";
    required: boolean;
    options?: string[];
    maxChoices?: number;
    placeholder?: string;
    maxLength?: number;
    ratingScale?: number;
    ratingStyle?: "numbers" | "stars" | "emojis";
    ratingMinLabel?: string;
    ratingMaxLabel?: string;
    validationType?: "email" | "phone" | "url" | "number" | "date";
    matrixRows?: Array<{ id: string; label: string }>;
    matrixColumns?: Array<{ id: string; label: string }>;
    matrixType?: "single" | "multiple";
    matrixColumnsNumeric?: boolean;
    selectedDates?: string[];
    timeSlotsByDate?: Record<
      string,
      Array<{ hour: number; minute: number; enabled: boolean }>
    >;
    timeGranularity?: "15min" | "30min" | "1h";
    allowMaybeVotes?: boolean;
    allowAnonymousVotes?: boolean;
    conditionalRules?: Array<{
      questionId: string;
      dependsOn: string;
      showIf: {
        operator:
          | "equals"
          | "contains"
          | "notEquals"
          | "isEmpty"
          | "isNotEmpty";
        value?: string | string[];
      };
    }>;
  }>;
}

export interface QuizzData extends BasePoll {
  type: "quizz";
  questions: Array<{
    id: string;
    question: string;
    type: "single" | "multiple" | "text" | "text-ai" | "true-false";
    options?: string[];
    correctAnswer: string | string[] | boolean;
    points?: number;
    explanation?: string;
  }>;
  allowAnonymousResponses?: boolean;
  showResultsAfterCompletion?: boolean;
  timeLimit?: number; // en minutes
}

export interface AvailabilityPollData extends BasePoll {
  type: "availability";
  clientAvailabilities?: string;
  parsedAvailabilities?: Array<{
    date: string;
    startTime: string;
    endTime: string;
  }>;
  proposedSlots?: Array<{
    date: string;
    startTime: string;
    endTime: string;
    score?: number;
  }>;
  settings?: {
    showLogo?: boolean;
    requireAuth?: boolean;
    oneResponsePerPerson?: boolean;
    allowEditAfterSubmit?: boolean;
    maxResponses?: number;
    resultsVisibility?: "creator-only" | "voters" | "public";
    minLatencyMinutes?: number;
    maxLatencyMinutes?: number;
    preferNearTerm?: boolean;
    preferHalfDays?: boolean;
    slotDurationMinutes?: number;
    autoConfirm?: boolean;
    reminders?: boolean;
  };
}

// Type union pour tous les polls
export type UnifiedPoll =
  | DatePollData
  | FormPollData
  | QuizzData
  | AvailabilityPollData;

// Interface pour les opérations CRUD
export interface PollServiceInterface {
  // CRUD Operations
  createPoll(
    pollData: Omit<UnifiedPoll, "id" | "created_at" | "updated_at">,
  ): Promise<UnifiedPoll>;
  getPoll(id: string): Promise<UnifiedPoll | null>;
  getPollBySlug(slug: string): Promise<UnifiedPoll | null>;
  updatePoll(
    id: string,
    updates: Partial<UnifiedPoll>,
    options?: UpdatePollOptions,
  ): Promise<UnifiedPoll>;
  deletePoll(id: string): Promise<void>;

  // Query Operations
  getPollsByUser(userId: string): Promise<UnifiedPoll[]>;
  migrateGuestPollsToUser(userId: string, fingerprint: string): Promise<number>;
  getPollsByType(type: PollType): Promise<UnifiedPoll[]>;
  searchPolls(query: string, userId?: string): Promise<UnifiedPoll[]>;

  // Validation
  validatePollData(pollData: PollCreateInput): {
    isValid: boolean;
    errors: string[];
  };
  validateUpdateData(updates: Partial<UnifiedPoll>): {
    isValid: boolean;
    errors: string[];
  };

  // Transformations
  transformToLegacyFormat(poll: UnifiedPoll): Poll;
  transformFromLegacyFormat(poll: Poll): UnifiedPoll;
}

/**
 * Échec du rattachement explicite d'un sondage invité. `rate_limited` :
 * plafond de `claim_guest_polls` (10/h/utilisateur) atteint.
 */
export class GuestPollClaimError extends Error {
  readonly reason: "rate_limited" | "failed";

  constructor(reason: "rate_limited" | "failed", message: string) {
    super(message);
    this.reason = reason;
    this.name = "GuestPollClaimError";
  }
}

/**
 * PollService Unifié
 *
 * ATTENTION : Ce service est CRITIQUE pour l'architecture unifiée des polls.
 * NE MODIFIEZ AUCUN ÉLÉMENT SANS AUTORISATION EXPLICITE DE JULIEN.
 *
 * Service principal qui gère tous les types de polls de manière unifiée.
 * Délègue aux services spécialisés quand nécessaire pour la logique métier.
 *
 * Éléments critiques protégés :
 * - Structure poll_data_json dans la base de données
 * - Méthodes createPoll() et getPollBySlug()
 * - Logique de transformation vers UnifiedPoll
 * - Fusion intelligente des données de base + poll_data_json
 */
export interface UpdatePollOptions {
  /**
   * Correspondance ancien → nouveau numéro d'option (lib/voteRemap), quand les
   * dates ou créneaux d'un sondage DÉJÀ VOTÉ changent. Les votes sont alors
   * recâblés par la base dans la même transaction que la mise à jour.
   */
  voteIdMap?: Record<string, string | null> | null;
}

export class PollService implements PollServiceInterface {
  private static instance: PollService;

  // Dédup des appels concurrents `getPollsByUser` pour le même userId :
  // plusieurs hooks (useQuota, useDataControl, usePolls…) montés en parallèle
  // déclenchaient chacun la même requête au chargement.
  private inflightPollsByUser = new Map<string, Promise<UnifiedPoll[]>>();

  private constructor() {}

  public static getInstance(): PollService {
    if (!PollService.instance) {
      PollService.instance = new PollService();
    }
    return PollService.instance;
  }

  /**
   * Créer un nouveau poll
   *
   * ATTENTION : Cette méthode est CRITIQUE.
   * NE MODIFIEZ PAS la structure poll_data_json sans autorisation.
   * Toutes les données spécifiques DOIVENT aller dans poll_data_json.
   */
  async createPoll(pollData: PollCreateInput): Promise<UnifiedPoll> {
    try {
      logger.info(`Creating ${pollData.type} poll: ${pollData.title}`);

      // Validation des données
      const validation = this.validatePollData(pollData);
      if (!validation.isValid) {
        throw ErrorFactory.validation(
          `Invalid poll data: ${validation.errors.join(", ")}`,
        );
      }

      // Issue #759 — trier selectedDates en ordre chronologique avant persistance.
      // L'ordre des clics utilisateur n'est pas une donnée métier ; le rendu
      // (vote / résultats / exports) doit refléter l'ordre du temps. Sort
      // également la copie dupliquée dans settings.selectedDates pour cohérence.
      const normalizedPollData = (() => {
        type DpShape = PollCreateInput & {
          selectedDates?: string[];
          settings?: { selectedDates?: string[] } & Record<string, unknown>;
        };
        const dp = pollData as DpShape;
        const hasTop =
          Array.isArray(dp.selectedDates) && dp.selectedDates.length > 1;
        const hasNested =
          Array.isArray(dp.settings?.selectedDates) &&
          (dp.settings?.selectedDates?.length ?? 0) > 1;
        if (!hasTop && !hasNested) {
          return pollData;
        }
        const next: DpShape = { ...dp };
        if (hasTop) {
          next.selectedDates = [...(dp.selectedDates as string[])].sort();
        }
        if (hasNested) {
          next.settings = {
            ...(dp.settings ?? {}),
            selectedDates: [
              ...(dp.settings as { selectedDates: string[] }).selectedDates,
            ].sort(),
          };
        }
        return next;
      })();

      // Bug hunt 2026-04-23 — etape B : passer par la RPC create_poll
      // (SECURITY DEFINER) au lieu de .insert().select() direct, pour
      // pouvoir ensuite DROPper les policies SELECT permissives sur polls.
      // Phase 1 security fix #859: Use Edge Function wrapper instead of direct RPC
      let data, error;
      try {
        data = await createPollViaEdgeFunction({
          type: normalizedPollData.type,
          title: normalizedPollData.title,
          slug: normalizedPollData.slug,
          poll_data_json: stripCreatorIdentity(
            normalizedPollData as unknown as Record<string, unknown>,
          ),
          description: pollData.description ?? undefined,
          guest_fingerprint: pollData.guest_fingerprint ?? undefined,
          guest_session_id:
            (pollData as { guest_session_id?: string }).guest_session_id ??
            undefined,
          status: pollData.status || "draft",
        });
        error = null;
      } catch (err) {
        error = err instanceof Error ? err : new Error(String(err));
        data = null;
      }

      if (error || !data) {
        const isSlugCollision =
          (error as { code?: string } | null)?.code === "23505" ||
          /polls_slug_key|duplicate key/i.test(error?.message || "");
        const typeLabel =
          pollData.type === "form"
            ? "form"
            : pollData.type === "date"
              ? "date poll"
              : pollData.type === "quizz"
                ? "quizz"
                : "poll";
        const err = ErrorFactory.storage(
          `Failed to create ${typeLabel}: ${error?.message || "no data returned"}`,
        );
        if (isSlugCollision) {
          (err as Error & { code?: string }).code = "SLUG_COLLISION";
        }
        throw err;
      }

      logger.info(`Successfully created poll: ${data.id}`);

      // i18n : persister la langue du créateur sur le sondage (sert à choisir
      // la langue des emails transactionnels). La colonne a 'fr' par défaut,
      // donc on n'écrit que pour les langues non-fr (en, es).
      const creatorLang = i18nInstance.language?.startsWith("en")
        ? "en"
        : i18nInstance.language?.startsWith("es")
          ? "es"
          : "fr";
      if (data.id && creatorLang !== "fr") {
        try {
          await supabase
            .from("polls")
            .update({ language: creatorLang })
            .eq("id", data.id);
        } catch {
          // best-effort : fallback 'fr' acceptable si l'update échoue (RLS invité)
        }
      }

      // Fusionner les données de base avec poll_data_json pour retourner l'objet complet
      const unifiedData = {
        ...data.poll_data_json,
        ...creatorIdentityFromRow(data),
        id: data.id,
        created_at: data.created_at,
        updated_at: data.updated_at,
      };

      return unifiedData as UnifiedPoll;
    } catch (error) {
      logError(error as Error, {
        component: "PollService",
        operation: "createPoll",
        pollData,
      });

      // ⚠️ DEPRECATED - COMMENTÉ (Phase 3.2 - 01/04/2026)
      // Ce fallback localStorage viole les règles de stockage (STORAGE-ARCHITECTURE-FINAL.md)
      // Raison: localStorage interdit pour données métier, Supabase uniquement
      // En cas d'erreur Supabase, afficher un message d'erreur à l'utilisateur, pas de fallback
      /*
      try {
        console.log("[PollService] Supabase failed, trying localStorage fallback for createPoll");
        const { enrichPollForGuest } = await import("@core/lib/guestIdentityService");
        const { validatePoll } = await import("@core/lib/pollStorage");
        
        // Créer le poll directement dans localStorage
        const enrichedPoll = await enrichPollForGuest(pollData);
        const pollForValidation = {
          ...pollData,
          ...enrichedPoll,
          id: crypto.randomUUID(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          status: pollData.status || 'draft',
        };
        validatePoll(pollForValidation);
        
        // Simuler le retour de Supabase avec le poll enrichi
        const unifiedPoll = {
          ...pollData,
          ...enrichedPoll,
          id: crypto.randomUUID(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          status: pollData.status || 'draft',
        } as UnifiedPoll;
        
        console.log("[PollService] localStorage createPoll successful:", !!unifiedPoll);
        return unifiedPoll;
      } catch (e) {
        console.error("[PollService] localStorage fallback error for createPoll:", e);
      }
      */

      throw error;
    }
  }

  /**
   * Récupérer un poll par ID
   */
  async getPoll(id: string): Promise<UnifiedPoll | null> {
    try {
      let { data, error } = await supabase
        .from("polls")
        .select("*")
        .eq("id", id)
        .single();

      // Fallback RPC SECURITY DEFINER si la RLS SELECT bloque (sondage invité,
      // creator_id NULL → l'anon ne le voit pas en SELECT direct). Permet
      // l'édition d'un sondage invité (bug "Modifier → /dashboard", 01/06).
      if (error || !data) {
        // Le RPC masque les PII (guest_fingerprint, creator_id) et sanitise
        // poll_data_json tant que l'appelant ne prouve pas être le créateur.
        // On fournit donc les deux preuves disponibles côté client — sinon
        // l'éditeur rouvrirait un sondage invité amputé de ses réglages.
        let callerFingerprint: string | null = null;
        try {
          const { getFingerprint } =
            await import("@core/lib/guestIdentityService");
          callerFingerprint = await getFingerprint();
        } catch {
          // Preuve optionnelle : NULL = "pas d'identité prouvée".
        }
        // Clés ajoutées seulement si renseignées : PostgREST résout par
        // l'ensemble exact des arguments nommés, et le front se déploie en
        // parallèle des migrations. Un visiteur sans preuve reste ainsi
        // compatible avec l'ancienne signature.
        // Jeton du trousseau pour CE sondage s'il existe (lien d'admin), sinon
        // le jeton principal du navigateur.
        const ownerToken = getOwnerTokenForPoll(id);
        const rpc = await supabase.rpc("get_poll_by_id", {
          p_id: id,
          ...(callerFingerprint
            ? { p_caller_fingerprint: callerFingerprint }
            : {}),
          ...(ownerToken ? { p_owner_token: ownerToken } : {}),
        });
        const rpcRow = Array.isArray(rpc.data) ? rpc.data[0] : rpc.data;
        if (!rpc.error && rpcRow) {
          data = rpcRow;
          error = null;
        }
      }

      if (error || !data) {
        // ⚠️ DEPRECATED - COMMENTÉ (Phase 3.2 - 01/04/2026)
        // Ce fallback localStorage viole les règles de stockage (STORAGE-ARCHITECTURE-FINAL.md)
        // Raison: localStorage interdit pour données métier, Supabase uniquement
        /*
        try {
          console.log(
            "[PollService] ID not found in Supabase, trying localStorage fallback for ID:",
            id,
          );
          const { getPollBySlugOrId } = await import("@core/lib/pollStorage");
          const localPoll = getPollBySlugOrId(id);
          console.log(
            "[PollService] localStorage result for ID:",
            id,
            !!localPoll,
          );
          if (localPoll) return localPoll as unknown as UnifiedPoll;
        } catch (e) {
          console.error("[PollService] localStorage fallback error:", e);
        }
        */
        return null;
      }

      // Fusionner les données de base avec poll_data_json (colonnes top-level
      // = source de vérité, écrasent poll_data_json).
      return mergePollRow(data) as unknown as UnifiedPoll;
    } catch (error) {
      logError(error as Error, {
        component: "PollService",
        operation: "getPoll",
      });

      // ⚠️ DEPRECATED - COMMENTÉ (Phase 3.2 - 01/04/2026)
      // Ce fallback localStorage viole les règles de stockage (STORAGE-ARCHITECTURE-FINAL.md)
      // Raison: localStorage interdit pour données métier, Supabase uniquement
      /*
      try {
        const { getPollBySlugOrId } = await import("@core/lib/pollStorage");
        const localPoll = getPollBySlugOrId(id);
        if (localPoll) return localPoll as unknown as UnifiedPoll;
      } catch (e) {
        // Ignorer l'erreur d'import ou de lecture
      }
      */

      return null;
    }
  }

  /**
   * Récupérer un poll par son slug
   *
   * ATTENTION : Cette méthode est CRITIQUE.
   * La transformation vers UnifiedPoll doit TOUJOURS préserver
   * la logique de fusion intelligente (base + poll_data_json).
   */
  async getPollBySlug(slug: string): Promise<UnifiedPoll | null> {
    try {
      // RPC SECURITY DEFINER : lockdown RLS anon (bug hunt 2026-04-23).
      // Remplace `from("polls").select().eq("slug")` — la policy permissive
      // sur polls (USING true) sera retirée en étape 1c.
      //
      // Bug hunt nuit #3 (2026-04-29) : on transmet le fingerprint local
      // pour permettre au RPC de révéler `guest_fingerprint` /
      // `creator_id` UNIQUEMENT au vrai créateur. Pour un visiteur lambda
      // ces champs reviennent à NULL, ce qui casse la chain attack
      // get_poll_by_slug → get_polls_by_guest.
      let callerFingerprint: string | null = null;
      try {
        const { getFingerprint } =
          await import("@core/lib/guestIdentityService");
        callerFingerprint = await getFingerprint();
      } catch {
        // Le fingerprint est optionnel — on appelle sans en cas d'erreur,
        // le RPC traite NULL comme "pas de preuve d'identité".
      }
      const slugOwnerToken = getOwnerTokenForPoll(null, slug);
      const { data: rpcData, error } = await supabase
        .rpc("get_poll_by_slug", {
          p_slug: slug,
          p_caller_fingerprint: callerFingerprint,
          ...(slugOwnerToken ? { p_owner_token: slugOwnerToken } : {}),
        })
        .maybeSingle();
      const data = rpcData as any;

      if (error || !data) {
        // ⚠️ DEPRECATED - COMMENTÉ (Phase 3.2 - 01/04/2026)
        // Ce fallback localStorage viole les règles de stockage (STORAGE-ARCHITECTURE-FINAL.md)
        // Raison: localStorage interdit pour données métier, Supabase uniquement
        /*
        try {
          console.log(
            "[PollService] Slug not found in Supabase, trying localStorage fallback for slug:",
            slug,
          );
          const { getPollBySlugOrId } = await import("@core/lib/pollStorage");
          const localPoll = getPollBySlugOrId(slug);
          console.log(
            "[PollService] localStorage result for slug:",
            slug,
            !!localPoll,
          );
          if (localPoll) return localPoll as unknown as UnifiedPoll;
        } catch (e) {
          console.error("[PollService] localStorage fallback error:", e);
        }
        */
        return null;
      }

      // Fusionner intelligemment les données de base avec poll_data_json
      const pollData = data.poll_data_json || {};

      const unifiedData = {
        // Préserver les champs de la base de données en priorité
        id: data.id,
        title: data.title || pollData.title,
        slug: data.slug || pollData.slug,
        selectedDates: pollData.selectedDates, // Uniquement depuis poll_data_json
        type: data.type || pollData.type,
        status: data.status || pollData.status,
        creator_type: data.creator_type || pollData.creator_type,
        creator_id: data.creator_id || pollData.creator_id,
        guest_fingerprint: data.guest_fingerprint || pollData.guest_fingerprint,
        created_at: data.created_at,
        updated_at: data.updated_at,

        // Message du créateur aux participants, affiché sous le titre du vote.
        description: data.description ?? pollData.description ?? null,

        // Fusionner les autres champs de poll_data_json (sans écraser)
        questions: pollData.questions, // ⚠️ Requis pour les sondages form/quizz
        settings: pollData.settings,
        timeSlotsByDate: pollData.timeSlotsByDate,
        dates: pollData.dates,
        deadline: pollData.deadline,
        // Mode Typeform + thème + visibilité résultats + règles conditionnelles
        // Check settings first (form polls store config in settings), then top-level
        displayMode: pollData.settings?.displayMode ?? pollData.displayMode,
        themeId: pollData.settings?.themeId ?? pollData.themeId,
        resultsVisibility:
          pollData.settings?.resultsVisibility ?? pollData.resultsVisibility,
        conditionalRules:
          pollData.settings?.conditionalRules ?? pollData.conditionalRules,
        // Template de branding (Story 1 — couleurs/logo appliqués sur la
        // page de vote/résultats). Joint côté RPC get_poll_by_slug pour
        // contourner la RLS own-rows-only de poll_templates.
        template: data.template_id
          ? {
              id: data.template_id,
              name: data.template_name,
              primary_color: data.template_primary_color,
              secondary_color: data.template_secondary_color,
              logo_url: data.template_logo_url,
            }
          : null,
        // Date fixée par l'organisateur (RPC set_poll_final_date, spec S3) :
        // lue par la page de vote (« Date retenue ») et les résultats.
        finalDate: pollData.finalDate ?? null,
        // ... autres champs poll_data_json
      };

      console.log("=== GETPOLLBYSLUG RÉSULTAT ===");
      console.log("unifiedData:", unifiedData);
      console.log("unifiedData.selectedDates:", unifiedData.selectedDates);

      return unifiedData as UnifiedPoll;
    } catch (error) {
      logError(error as Error, {
        component: "PollService",
        operation: "getPollBySlug",
      });

      // ⚠️ DEPRECATED - COMMENTÉ (Phase 3.2 - 01/04/2026)
      // Ce fallback localStorage viole les règles de stockage (STORAGE-ARCHITECTURE-FINAL.md)
      // Raison: localStorage interdit pour données métier, Supabase uniquement
      /*
      try {
        const { getPollBySlugOrId } = await import("@core/lib/pollStorage");
        const localPoll = getPollBySlugOrId(slug);
        if (localPoll) return localPoll as unknown as UnifiedPoll;
      } catch (e) {
        // Ignorer l'erreur d'import ou de lecture
      }
      */

      return null;
    }
  }

  /**
   * Génère un slug à partir d'un titre
   */
  private generateSlugFromTitle(title: string): string {
    if (!title) return `poll-${Date.now()}`;

    // Bug hunt 2026-04-23 #8 : strip HTML avant slugification pour éviter
    // de conserver les noms de balises (ex. `<script>` → plus de "script"
    // dans le slug).
    const stripped =
      typeof document !== "undefined"
        ? new DOMParser().parseFromString(title, "text/html").body
            .textContent || ""
        : title.replace(/<[^>]*>/g, "");

    // Convertir le titre en slug
    let slug = stripped
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "") // Garder seulement lettres, chiffres, espaces et tirets
      .replace(/\s+/g, "-") // Remplacer les espaces par des tirets
      .replace(/-+/g, "-") // Éviter les tirets multiples
      .replace(/^-+|-+$/g, "") // Pas de tiret en début/fin
      .trim();

    // Limiter la longueur (40 au lieu de 50 : coupe plus court les résidus de payload)
    slug = slug.substring(0, 40);

    // Si le slug est vide, utiliser un fallback
    if (!slug) {
      slug = `poll-${Date.now()}`;
    }

    return slug;
  }

  /**
   * Duplique les données spécifiques d'un sondage de dates
   * Copie TOUTES les données sauf les votes (participantEmails)
   */
  private duplicateDatePollData(original: DatePollData): Partial<DatePollData> {
    return {
      type: "date",
      // Copier toutes les dates
      selectedDates: original.selectedDates ? [...original.selectedDates] : [],
      // Deep copy des créneaux horaires
      timeSlotsByDate: original.timeSlotsByDate
        ? JSON.parse(JSON.stringify(original.timeSlotsByDate))
        : undefined,
      // Deep copy des groupes de dates
      dateGroups: original.dateGroups
        ? JSON.parse(JSON.stringify(original.dateGroups))
        : undefined,
      // Copier les paramètres
      showTimeSlots: original.showTimeSlots,
      notificationsEnabled: original.notificationsEnabled,
      // Copier les emails invités (liste d'invitation, pas les votes)
      invitedEmails: original.invitedEmails
        ? [...original.invitedEmails]
        : undefined,
      // NE PAS copier participantEmails (ce sont les votes)
    };
  }

  /**
   * Duplique les données spécifiques d'un formulaire
   * Copie toutes les questions avec leurs options et règles conditionnelles
   */
  private duplicateFormPollData(original: FormPollData): Partial<FormPollData> {
    return {
      type: "form",
      // Deep copy de toutes les questions avec leurs propriétés complètes
      questions: original.questions
        ? JSON.parse(JSON.stringify(original.questions))
        : [],
    };
  }

  /**
   * Duplique les données spécifiques d'un quizz
   * Copie toutes les questions avec réponses correctes et paramètres
   */
  private duplicateQuizzData(original: QuizzData): Partial<QuizzData> {
    return {
      type: "quizz",
      // Deep copy des questions avec réponses correctes
      questions: original.questions
        ? JSON.parse(JSON.stringify(original.questions))
        : [],
      // Copier les paramètres
      allowAnonymousResponses: original.allowAnonymousResponses,
      showResultsAfterCompletion: original.showResultsAfterCompletion,
      timeLimit: original.timeLimit,
    };
  }

  /**
   * Duplique les données spécifiques d'un sondage de disponibilités
   * Copie la structure des disponibilités et créneaux proposés, pas les réponses
   */
  private duplicateAvailabilityPollData(
    original: AvailabilityPollData,
  ): Partial<AvailabilityPollData> {
    return {
      type: "availability",
      // Deep copy des disponibilités parsées (structure)
      parsedAvailabilities: original.parsedAvailabilities
        ? JSON.parse(JSON.stringify(original.parsedAvailabilities))
        : undefined,
      // Deep copy des créneaux proposés
      proposedSlots: original.proposedSlots
        ? JSON.parse(JSON.stringify(original.proposedSlots))
        : undefined,
      // NE PAS copier clientAvailabilities (réponses des participants)
      // NE PAS copier validatedSlot (résultat final)
    };
  }

  /**
   * Duplique un sondage dans Supabase
   * Copie TOUTES les données (titre, dates, heures, options) SAUF les votes
   */
  async duplicatePoll(poll: UnifiedPoll): Promise<UnifiedPoll> {
    try {
      // Validation des entrées
      if (!poll.id || !poll.type) {
        throw ErrorFactory.validation(
          "Poll ID and type are required for duplication",
        );
      }

      // Générer un slug si le poll n'en a pas
      const originalSlug = poll.slug || this.generateSlugFromTitle(poll.title);

      // Créer le nouveau slug unique
      const baseSlug = originalSlug.replace(/-copy-\d+$/, "");
      const newSlug = `${baseSlug}-copy-${Date.now()}`;

      // Détecter si c'est un poll guest et enrichir avec guest_fingerprint
      let guestFingerprint = null;
      const creatorType = poll.creator_id ? "user" : "guest";

      if (!poll.creator_id) {
        // C'est un poll guest, on doit générer un fingerprint
        const { getGuestIdentity } =
          await import("@core/lib/guestIdentityService");
        const { fingerprint } = await getGuestIdentity();
        guestFingerprint = fingerprint;
        logger.info("Generated guest fingerprint for duplication", "poll", {
          fingerprint: fingerprint.substring(0, 16),
        });
      }

      // Duplication spécialisée selon le type de poll
      let duplicatedData: Partial<UnifiedPoll>;

      switch (poll.type) {
        case "date":
          duplicatedData = this.duplicateDatePollData(poll as DatePollData);
          break;
        case "form":
          duplicatedData = this.duplicateFormPollData(poll as FormPollData);
          break;
        case "quizz":
          duplicatedData = this.duplicateQuizzData(poll as QuizzData);
          break;
        case "availability":
          duplicatedData = this.duplicateAvailabilityPollData(
            poll as AvailabilityPollData,
          );
          break;
        default: {
          // Ce cas ne devrait jamais arriver avec les types corrects
          const exhaustiveCheck: never = poll;
          throw ErrorFactory.validation(
            `Unknown poll type: ${(exhaustiveCheck as any).type}`,
          );
        }
      }

      const duplicatedPollDataJson = {
        ...duplicatedData,
        title: `${poll.title} (Copie)`,
        slug: newSlug,
        // Champs communs à tous les types (BasePoll) : le message aux
        // participants et les réglages avancés (lieu, expiration, audience
        // e-mails, votes vérifiés, visibilité des résultats, etc.) ne sont
        // portés par aucune des fonctions duplicate*PollData ci-dessus,
        // qui ne copient que les champs spécifiques au type.
        description: poll.description,
        settings: poll.settings
          ? JSON.parse(JSON.stringify(poll.settings))
          : undefined,
        metadata: {
          source: "duplicate",
          originalId: poll.id,
          duplicatedAt: new Date().toISOString(),
        },
      };

      // Bug hunt 2026-04-23 — etape B : via RPC create_poll (SECURITY DEFINER).
      const { data, error } = await supabase.rpc("create_poll", {
        p_type: poll.type,
        p_title: `${poll.title} (Copie)`,
        p_slug: newSlug,
        p_poll_data_json: stripCreatorIdentity(
          duplicatedPollDataJson as unknown as Record<string, unknown>,
        ),
        p_creator_id: poll.creator_id || null,
        p_creator_type: creatorType,
        p_guest_fingerprint: guestFingerprint ?? null,
        p_status: "draft",
        p_guest_owner_token: getGuestOwnerToken(),
      });

      if (error || !data) {
        logError(error ?? new Error("no data"), {
          component: "PollService",
          operation: "duplicatePoll",
          pollId: poll.id,
        });
        throw ErrorFactory.storage(
          `Impossible de dupliquer le sondage: ${error?.message || "no data"}`,
        );
      }

      // Fusionner les données de base avec poll_data_json pour le format unifié
      const pollData = data.poll_data_json || {};
      const unifiedData = {
        ...pollData,
        id: data.id,
        title: data.title || pollData.title,
        slug: data.slug || pollData.slug,
        type: data.type || pollData.type,
        status: data.status || pollData.status,
        creator_type: data.creator_type || pollData.creator_type,
        creator_id: data.creator_id || pollData.creator_id,
        guest_fingerprint: data.guest_fingerprint || pollData.guest_fingerprint,
        created_at: data.created_at,
        updated_at: data.updated_at,
      };

      logger.info("Poll duplicated successfully", "poll", {
        originalId: poll.id,
        newId: data.id,
        newSlug,
        type: poll.type,
      });

      return unifiedData as UnifiedPoll;
    } catch (error) {
      logError(error as Error, {
        component: "PollService",
        operation: "duplicatePoll",
        pollId: poll.id,
      });

      throw error;
    }
  }

  /**
   * Mettre à jour un poll
   */
  async updatePoll(
    id: string,
    updates: Partial<UnifiedPoll>,
    options: UpdatePollOptions = {},
  ): Promise<UnifiedPoll> {
    try {
      logger.info(`Updating poll ${id}`);

      // Validation des données
      const validation = this.validateUpdateData(updates);
      if (!validation.isValid) {
        throw ErrorFactory.validation(
          `Invalid update data: ${validation.errors.join(", ")}`,
        );
      }

      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      // ⚠️ `getCurrentUserId` retombe sur le device id : il ne renvoie JAMAIS
      // null, même sans compte. S'en servir pour distinguer un invité d'un
      // utilisateur connecté rendait la branche invité ci-dessous morte, et
      // envoyait tout enregistrement d'invité vers le SELECT direct sur
      // `polls` — fermé par la RLS, donc 406 « Cannot coerce the result to a
      // single JSON object » (constat de recette 2026-09-16, 2ᵉ enregistrement
      // d'un brouillon invité). On garde les deux notions séparées.
      const authUserId = authUser?.id ?? null;

      // Mutation en deux temps :
      //   a) Changement de statut → RPC SECURITY DEFINER `set_poll_status`.
      //      Indispensable pour les invités : ils n'ont plus de policy SELECT sur
      //      `polls` (lockdown 2026-04-23), donc ni un SELECT ni un UPDATE direct
      //      ne voient leur propre sondage → archiver/terminer échouait en
      //      silence et le sondage restait votable. Le RPC bypass la RLS tout en
      //      re-vérifiant la propriété (auth.uid() OU guest_fingerprint).
      //   b) Autres champs (title/description/poll_data_json) → SELECT + UPDATE
      //      direct, réservés aux utilisateurs connectés (couverts par la RLS).
      const hasStatus = updates.status !== undefined;
      const otherKeys = Object.keys(updates).filter((k) => k !== "status");
      const hasOtherFields = otherKeys.length > 0;

      let data: any = null;

      if (hasStatus) {
        let callerFingerprint: string | null = null;
        try {
          const { getFingerprint } =
            await import("@core/lib/guestIdentityService");
          callerFingerprint = await getFingerprint();
        } catch {
          // Fingerprint optionnel : null si indisponible (le RPC refusera alors
          // les invités, mais laissera passer les utilisateurs connectés).
        }

        const { data: rpcData, error: rpcError } = await supabase.rpc(
          "set_poll_status",
          {
            p_poll_id: id,
            p_status: updates.status,
            p_fingerprint: callerFingerprint,
            p_owner_token: getOwnerTokenForPoll(id),
          },
        );

        if (rpcError || !rpcData) {
          throw ErrorFactory.storage(
            `Failed to update poll status: ${rpcError?.message || "Unknown error"}`,
          );
        }
        // `rpcData` peut être un objet (RETURNS polls) ou un tableau selon le client.
        data = Array.isArray(rpcData) ? rpcData[0] : rpcData;
      }

      // Créateur SANS COMPTE (jeton de propriétaire de CE sondage dans ce
      // navigateur) : la RLS `polls_update_owner_only` est réservée à
      // `authenticated`, l'UPDATE direct ci-dessous lui est fermé.
      // `update_guest_poll` (2026-10-02) accepte brouillon, actif et clos —
      // le créateur invité corrige son sondage publié au lieu de le recréer.
      // La base vérifie le jeton ; sans jeton, aucune voie invité.
      // Tenté aussi pour un utilisateur connecté qui détient le jeton (sondage
      // invité ouvert depuis son navigateur) ; si la base refuse (sondage
      // rattaché à un compte), on retombe sur la voie du compte.
      const draftOwnerToken = getOwnerTokenForPoll(id);
      let guestHandled = false;

      // Options d'un sondage DÉJÀ VOTÉ modifiées : une seule RPC met à jour
      // le sondage ET recâble les votes (même transaction) — compte
      // propriétaire ou créateur invité muni du jeton. Jamais de repli sur un
      // enregistrement simple : il décalerait les votes en silence.
      if (hasOtherFields && options.voteIdMap) {
        const { data: remapData, error: remapError } = await supabase.rpc(
          "update_poll_remapping_votes",
          {
            p_poll_id: id,
            ...(draftOwnerToken ? { p_owner_token: draftOwnerToken } : {}),
            p_title: updates.title ?? null,
            p_description: updates.description ?? null,
            p_poll_data_json: mergeUpdatesIntoPollData(
              null,
              updates as Record<string, unknown>,
            ),
            p_id_map: options.voteIdMap,
          },
        );
        if (remapError) {
          throw ErrorFactory.storage(
            `Failed to update poll and remap votes: ${remapError.message}`,
            i18nInstance.t(
              remapError.code === "PGRST202"
                ? "creator:edit_voted_poll_unavailable"
                : "creator:edit_voted_poll_failed",
            ),
          );
        }
        data = Array.isArray(remapData) ? remapData[0] : remapData;
        if (!data) {
          throw ErrorFactory.storage(
            "Failed to update poll and remap votes: no row returned",
            i18nInstance.t("creator:edit_voted_poll_failed"),
          );
        }
        guestHandled = true;
      }

      if (hasOtherFields && draftOwnerToken && !guestHandled) {
        const guestArgs = {
          p_poll_id: id,
          p_owner_token: draftOwnerToken,
          p_title: updates.title ?? null,
          p_description: updates.description ?? null,
          p_poll_data_json: mergeUpdatesIntoPollData(
            null,
            updates as Record<string, unknown>,
          ),
        };
        let { data: draftData, error: draftError } = await supabase.rpc(
          "update_guest_poll",
          guestArgs,
        );
        // Front déployé avant la migration (Vercel et db-migrate partent en
        // parallèle, CLAUDE.md) : la RPC n'existe pas encore → ancienne voie,
        // limitée aux brouillons.
        if (draftError?.code === "PGRST202") {
          ({ data: draftData, error: draftError } = await supabase.rpc(
            "update_guest_poll_draft",
            guestArgs,
          ));
        }
        const refusedForAccount = !!authUserId && draftError?.code === "42501";
        if (!refusedForAccount) {
          guestHandled = true;

          if (draftError) {
            // « only drafts » ne survient plus que pendant la fenêtre de
            // déploiement (repli sur `update_guest_poll_draft`, limitée aux
            // brouillons). Le dire clairement plutôt que « impossible
            // d'enregistrer », qui laisse croire à une panne passagère.
            const isPublished = draftError.message.includes("only drafts");
            throw ErrorFactory.storage(
              `Failed to update guest draft: ${draftError.message}`,
              isPublished
                ? i18nInstance.t("creator:draft_published_guest_error")
                : "Impossible d'enregistrer ce brouillon.",
            );
          }

          data = Array.isArray(draftData) ? draftData[0] : draftData;
          if (!data) {
            throw ErrorFactory.storage(
              "Failed to update guest draft: no row returned",
              "Impossible d'enregistrer ce brouillon.",
            );
          }
        }
      }
      if (hasOtherFields && !guestHandled) {
        // 🔒 Defense-in-depth : charger le sondage et vérifier la propriété
        // AVANT l'UPDATE. La RLS reste le gardien final ; cette couche donne une
        // erreur claire. Réservé aux utilisateurs connectés (les invités n'ont
        // pas de policy SELECT — l'édition de champs hors statut leur est de
        // toute façon fermée).
        const { data: existingPoll, error: fetchError } = await supabase
          .from("polls")
          .select("creator_id, poll_data_json")
          .eq("id", id)
          .single();

        if (fetchError || !existingPoll) {
          throw ErrorFactory.storage(
            `Failed to load poll for ownership check: ${fetchError?.message || "Not found"}`,
            "Ce sondage n'existe plus ou vous n'y avez pas accès.",
          );
        }

        if (
          !existingPoll.creator_id ||
          existingPoll.creator_id !== authUserId
        ) {
          logger.warn(
            `Unauthorized updatePoll attempt: pollId=${id}, actor=${authUserId}, creator=${existingPoll.creator_id}`,
          );
          throw ErrorFactory.auth(
            `User ${authUserId} is not the creator of poll ${id}`,
            "Seul le créateur de ce sondage peut le modifier.",
          );
        }

        const { data: updData, error } = await supabase
          .from("polls")
          .update({
            title: updates.title,
            description: updates.description,
            // Merger les updates dans poll_data_json existant (ne pas écraser —
            // invariant R2 : questions du formulaire préservées si absentes des updates).
            poll_data_json: mergeUpdatesIntoPollData(
              existingPoll.poll_data_json as Record<string, unknown> | null,
              updates as Record<string, unknown>,
            ),
            updated_at: new Date().toISOString(),
          })
          .eq("id", id)
          .select()
          .single();

        if (error || !updData) {
          throw ErrorFactory.storage(
            `Failed to update poll: ${error?.message || "Unknown error"}`,
          );
        }
        data = updData;
      }

      if (!data) {
        throw ErrorFactory.storage("Failed to update poll: no changes applied");
      }

      logger.info(`Successfully updated poll: ${id}`);

      // Fusionner les données de base avec poll_data_json
      const pollData = data.poll_data_json || {};
      const unifiedData = {
        ...pollData,
        id: data.id,
        title: data.title || pollData.title,
        slug: data.slug || pollData.slug,
        type: data.type || pollData.type,
        status: data.status || pollData.status,
        creator_type: data.creator_type || pollData.creator_type,
        creator_id: data.creator_id || pollData.creator_id,
        guest_fingerprint: data.guest_fingerprint || pollData.guest_fingerprint,
        created_at: data.created_at,
        updated_at: data.updated_at,
      };

      return unifiedData as UnifiedPoll;
    } catch (error) {
      logError(error as Error, {
        component: "PollService",
        operation: "updatePoll",
      });
      throw error;
    }
  }

  /**
   * Supprimer un poll
   */
  async deletePoll(id: string): Promise<void> {
    try {
      logger.info(`Deleting poll: ${id}`);

      // Bug-hunt nuit #19 : un `.from('polls').delete()` direct touche 0 ligne
      // pour les sondages guest (pas de policy SELECT depuis le lockdown
      // 2026-04-23 → la pré-phase SELECT du DELETE PostgREST ne voit rien). On
      // passe par le RPC SECURITY DEFINER `delete_poll`, qui bypass la RLS mais
      // re-vérifie la propriété (auth.uid() OU guest_fingerprint), exactement
      // comme set_poll_status. Cf. 20260528_delete_poll_rpc.sql.
      let callerFingerprint: string | null = null;
      try {
        const { getFingerprint } =
          await import("@core/lib/guestIdentityService");
        callerFingerprint = await getFingerprint();
      } catch {
        // Fingerprint optionnel : null si indisponible. Le RPC refusera alors
        // les invités, mais laissera passer les utilisateurs connectés.
      }

      const { data, error } = await supabase.rpc("delete_poll", {
        p_poll_id: id,
        p_fingerprint: callerFingerprint,
        p_owner_token: getOwnerTokenForPoll(id),
      });

      if (error) {
        throw ErrorFactory.storage(`Failed to delete poll: ${error.message}`);
      }

      // `delete_poll` renvoie le nombre de lignes supprimées. 0 = le caller
      // n'est pas propriétaire (ou poll déjà absent) → on remonte une erreur
      // claire au lieu d'un faux succès silencieux.
      const deletedCount = typeof data === "number" ? data : Number(data ?? 0);
      if (deletedCount === 0) {
        throw ErrorFactory.storage(
          `Delete matched zero rows for poll ${id}`,
          "Ce sondage n'existe plus ou vous n'avez pas les droits pour le supprimer.",
        );
      }

      logger.info(`Successfully deleted poll: ${id}`);
    } catch (error) {
      logError(error as Error, {
        component: "PollService",
        operation: "deletePoll",
      });
      throw error;
    }
  }

  /**
   * Récupérer tous les polls d'un utilisateur.
   * Dédup en cas d'appels concurrents pour le même userId.
   */
  async getPollsByUser(userId: string): Promise<UnifiedPoll[]> {
    const existing = this.inflightPollsByUser.get(userId);
    if (existing) return existing;

    const promise = this._fetchPollsByUser(userId).finally(() => {
      this.inflightPollsByUser.delete(userId);
    });
    this.inflightPollsByUser.set(userId, promise);
    return promise;
  }

  /**
   * Compte les sondages "actifs" (`status === 'active'`) d'un utilisateur.
   *
   * Utilisé pour faire respecter le plafond `MAX_ACTIVE_POLLS.FREE = 10`.
   * Implémentation tolérante : si Supabase échoue, on retombe sur
   * `getPollsByUser` + filtre côté client (legacy fallback).
   */
  async countActivePollsByUser(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from("polls")
        .select("id", { count: "exact", head: true })
        .eq("creator_id", userId)
        .eq("status", "active");

      if (error) {
        logger.warn(
          "countActivePollsByUser: Supabase count failed, falling back to fetch+filter",
          "poll",
          { error: error.message },
        );
        throw error;
      }
      return count ?? 0;
    } catch {
      // Fallback : récupère tous les polls, filtre côté client.
      const polls = await this.getPollsByUser(userId);
      return polls.filter((p) => (p.status ?? "active") === "active").length;
    }
  }

  private async _fetchPollsByUser(userId: string): Promise<UnifiedPoll[]> {
    try {
      const { data, error } = await supabase
        .from("polls")
        .select("*")
        .eq("creator_id", userId)
        .order("created_at", { ascending: false });

      if (error) {
        throw ErrorFactory.storage(
          `Failed to fetch user polls: ${error.message}`,
        );
      }

      // Fusionner chaque poll avec ses poll_data_json
      const unifiedPolls = (data || []).map((poll) => ({
        ...poll.poll_data_json,
        ...creatorIdentityFromRow(poll),
        id: poll.id,
        created_at: poll.created_at,
        updated_at: poll.updated_at,
      }));

      return unifiedPolls as UnifiedPoll[];
    } catch (error) {
      logError(error as Error, {
        component: "PollService",
        operation: "getPollsByUser",
      });
      return [];
    }
  }

  /**
   * Migrer les polls invités (identifiés par fingerprint) vers un utilisateur connecté.
   * Retourne le nombre de polls migrés.
   */
  async migrateGuestPollsToUser(
    userId: string,
    fingerprint: string,
  ): Promise<number> {
    try {
      if (!userId || !fingerprint) {
        return 0;
      }

      // Bug hunt 2026-04-23 — etape C.1 : via RPC claim_guest_polls
      // (SECURITY DEFINER, auth.uid() -> creator_id). Prerequis pour fermer
      // la policy permissive "Anyone can view anonymous polls".
      const { data: migratedCount, error: rpcError } = await supabase.rpc(
        "claim_guest_polls",
        { p_fingerprint: fingerprint, p_owner_token: getGuestOwnerToken() },
      );

      if (rpcError) {
        if (
          rpcError.message.includes("signal is aborted") ||
          rpcError.message.includes("AbortError")
        ) {
          logger.warn(
            "Requête migration guest polls annulée (AbortError)",
            "poll",
            { fingerprint, error: rpcError.message },
          );
          return 0;
        }
        throw ErrorFactory.storage(
          `Failed to migrate guest polls: ${rpcError.message}`,
        );
      }

      // Jeton PRINCIPAL seulement (sondages créés dans ce navigateur). Les
      // jetons du trousseau (liens d'admin reçus) ne sont JAMAIS rattachés
      // automatiquement : SIGNED_IN est aussi émis à la restauration d'une
      // session, et un co-organisateur connecté qui ouvrait le lien d'admin
      // transféré s'appropriait le sondage à l'insu du créateur (28/09).
      // Rattachement explicite : `claimGuestPollWithToken`.
      const count = (migratedCount as number) ?? 0;
      if (count > 0) {
        logger.info("Guest polls migrated to authenticated user", "poll", {
          userId,
          migratedCount: count,
        });
      }

      return count;
    } catch (error) {
      logError(error as Error, {
        component: "PollService",
        operation: "migrateGuestPollsToUser",
        userId,
      });
      return 0;
    }
  }

  /**
   * Rattache EXPLICITEMENT au compte connecté le sondage dont le jeton d'admin
   * est dans le trousseau (lien d'admin reçu). Appelé uniquement sur action
   * de l'utilisateur : bouton « Rattacher ce sondage à mon compte », ou
   * intention posée par « Créer un compte » puis consommée après connexion.
   *
   * `p_fingerprint` doit être renseigné : la RPC l'écrit dans
   * `guest_poll_claim_audit.claimed_fingerprint` (NOT NULL). Avec `null`,
   * l'INSERT d'audit échoue et annule tout le rattachement — constaté en
   * recette locale le 28/09 (les mocks des tests unitaires ne le voyaient pas).
   *
   * RPC `claim_guest_poll` (20260929170000) : UN sondage précis, sur preuve
   * de SON jeton. Surtout pas `claim_guest_polls`, qui réclame tous les
   * sondages portant le même hash — un lien d'admin peut porter le jeton
   * principal du créateur, commun à tous ses sondages invités.
   *
   * Déploiement parallèle front/migration (CLAUDE.md, règle 2) : si la RPC
   * n'existe pas encore (PGRST202), AUCUN repli sur `claim_guest_polls` —
   * ce serait le rattachement multiple ; échec `failed`, l'utilisateur
   * réessaiera.
   *
   * Réussite (même à 0 : déjà rattaché) → les entrées du trousseau de CE
   * sondage (UUID, slug) sont retirées ; celles des autres sondages portant
   * le même jeton restent. Échec → `GuestPollClaimError` (`rate_limited` :
   * plafond de 10 appels/h partagé avec `claim_guest_polls`), trousseau
   * intact.
   */
  async claimGuestPollWithToken(
    pollId: string,
    token: string,
    fingerprint: string,
    slug?: string | null,
  ): Promise<number> {
    if (!pollId || !token || !fingerprint) {
      throw new GuestPollClaimError("failed", "missing claim arguments");
    }
    const { data, error } = await supabase.rpc("claim_guest_poll", {
      p_poll_id: pollId,
      p_owner_token: token,
      p_fingerprint: fingerprint,
    });
    if (error) {
      const rateLimited = error.message.includes("rate_limit_exceeded");
      logger.warn("Rattachement explicite d'un sondage invité refusé", "poll", {
        pollId,
        code: (error as { code?: string }).code,
        error: error.message,
      });
      throw new GuestPollClaimError(
        rateLimited ? "rate_limited" : "failed",
        error.message,
      );
    }
    removeKeyringKeys([pollId, ...(slug ? [slug] : [])]);
    const count = (data as number) ?? 0;
    logger.info("Guest poll explicitly claimed", "poll", { pollId, count });
    return count;
  }

  /**
   * Récupérer tous les polls d'un invité via guest_fingerprint
   */
  async getPollsByGuest(fingerprint: string): Promise<UnifiedPoll[]> {
    try {
      // Bug hunt 2026-04-23 — etape C.1 : via RPC get_polls_by_guest
      // (SECURITY DEFINER). Prerequis pour fermer les policies SELECT
      // permissives sur polls qui exposaient tout guest_fingerprint a anon.
      const { data, error } = await supabase.rpc("get_polls_by_guest", {
        p_fingerprint: fingerprint,
        p_owner_token: getGuestOwnerToken(),
      });

      if (error) {
        // Ignorer les erreurs Abort (signal is aborted without reason)
        if (
          error.message.includes("signal is aborted") ||
          error.message.includes("AbortError")
        ) {
          logger.warn("Requête guest polls annulée (AbortError)", "poll", {
            fingerprint,
            error: error.message,
          });
          return [];
        }

        throw ErrorFactory.storage(
          `Failed to fetch guest polls: ${error.message}`,
        );
      }

      // Fusionner chaque poll avec ses poll_data_json
      const unifiedPolls = (data || []).map((poll) => {
        // poll_data_json peut contenir des undefined, on fusionne intelligemment
        const pollData = poll.poll_data_json || {};

        return {
          // Préserver les champs de la base de données en priorité
          id: poll.id,
          title: poll.title || pollData.title,
          slug: poll.slug || pollData.slug,
          selectedDates: pollData.selectedDates, // Uniquement depuis poll_data_json
          type: poll.type || pollData.type,
          status: poll.status || pollData.status,
          creator_type: poll.creator_type || pollData.creator_type,
          guest_fingerprint:
            poll.guest_fingerprint || pollData.guest_fingerprint,
          created_at: poll.created_at,
          updated_at: poll.updated_at,

          description:
            (poll as { description?: string | null }).description ??
            pollData.description ??
            null,

          // Fusionner les autres champs de poll_data_json (sans écraser)
          settings: pollData.settings,
          timeSlotsByDate: pollData.timeSlotsByDate,
          dates: pollData.dates,
          deadline: pollData.deadline,
          // ... autres champs poll_data_json
        };
      });

      return unifiedPolls as UnifiedPoll[];
    } catch (error) {
      logError(error as Error, {
        component: "PollService",
        operation: "getPollsByGuest",
      });
      return [];
    }
  }

  /**
   * Récupérer tous les polls d'un type spécifique
   */
  async getPollsByType(type: PollType): Promise<UnifiedPoll[]> {
    try {
      const { data, error } = await supabase
        .from("polls")
        .select("*")
        .eq("type", type)
        .order("created_at", { ascending: false });

      if (error) {
        throw ErrorFactory.storage(
          `Failed to fetch polls by type: ${error.message}`,
        );
      }

      // Fusionner chaque poll avec ses poll_data_json
      const unifiedPolls = (data || []).map((poll) => ({
        ...poll.poll_data_json,
        ...creatorIdentityFromRow(poll),
        id: poll.id,
        created_at: poll.created_at,
        updated_at: poll.updated_at,
      }));

      return unifiedPolls as UnifiedPoll[];
    } catch (error) {
      logError(error as Error, {
        component: "PollService",
        operation: "getPollsByType",
      });
      return [];
    }
  }

  /**
   * Rechercher des polls
   */
  async searchPolls(query: string, userId?: string): Promise<UnifiedPoll[]> {
    try {
      let supabaseQuery = supabase
        .from("polls")
        .select("*")
        .or(`title.ilike.%${query}%,description.ilike.%${query}%`);

      if (userId) {
        supabaseQuery = supabaseQuery.eq("creator_id", userId);
      }

      const { data, error } = await supabaseQuery.order("created_at", {
        ascending: false,
      });

      if (error) {
        throw ErrorFactory.storage(`Failed to search polls: ${error.message}`);
      }

      // Fusionner chaque poll avec ses poll_data_json
      const unifiedPolls = (data || []).map((poll) => ({
        ...poll.poll_data_json,
        ...creatorIdentityFromRow(poll),
        id: poll.id,
        created_at: poll.created_at,
        updated_at: poll.updated_at,
      }));

      return unifiedPolls as UnifiedPoll[];
    } catch (error) {
      logError(error as Error, {
        component: "PollService",
        operation: "searchPolls",
      });
      return [];
    }
  }

  /**
   * Valider les données d'un poll
   */
  validatePollData(pollData: PollCreateInput): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Validation de base
    if (!pollData.title || pollData.title.trim().length === 0) {
      errors.push("Title is required");
    }

    if (!pollData.type) {
      errors.push("Poll type is required");
    }

    // Validation du créateur (authentifié ou invité)
    if (!pollData.creator_id && pollData.creator_type !== "guest") {
      errors.push("Creator ID is required for authenticated users");
    }

    // Validation spécifique par type
    if (pollData.type) {
      switch (pollData.type) {
        case "date": {
          const datePoll = pollData as Partial<DatePollData>;
          if (!datePoll.selectedDates || datePoll.selectedDates.length === 0) {
            errors.push("Date polls require at least one selected date");
          }
          break;
        }

        case "form": {
          const formPoll = pollData as Partial<FormPollData>;
          if (!formPoll.questions || formPoll.questions.length === 0) {
            errors.push("Form polls require at least one question");
          }
          break;
        }

        case "quizz": {
          const quizz = pollData as Partial<QuizzData>;
          if (!quizz.questions || quizz.questions.length === 0) {
            errors.push("Quizz polls require at least one question");
          }
          break;
        }

        case "availability":
          // Les availability polls peuvent être créés sans données initiales
          break;
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Valider les données de mise à jour
   */
  validateUpdateData(updates: Partial<UnifiedPoll>): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Validation de base pour les mises à jour
    if (updates.title && updates.title.trim().length === 0) {
      errors.push("Title cannot be empty");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Transformer vers le format legacy (compatibilité)
   */
  transformToLegacyFormat(poll: UnifiedPoll): Poll {
    // Implémentation pour la compatibilité avec le code existant
    // TODO: Implémenter la transformation complète
    return poll as unknown as Poll;
  }

  /**
   * Transformer depuis le format legacy
   */
  transformFromLegacyFormat(poll: Poll): UnifiedPoll {
    // Implémentation pour la compatibilité avec le code existant
    // TODO: Implémenter la transformation complète
    return poll as unknown as UnifiedPoll;
  }
}

// Export singleton
export const pollService = PollService.getInstance();
